import {
    isRouteErrorResponse,
    Links,
    Meta,
    Outlet,
    Scripts,
    ScrollRestoration,
} from "react-router";
import { ThemeProvider } from "@mui/material/styles";
import { CacheProvider } from "@emotion/react";
import CssBaseline from "@mui/material/CssBaseline";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import React from "react";
import createEmotionCache from "./utils/createEmotionCache";
import sIcon from "~/images/s.svg";

import type { Route } from "./+types/root";
import "./app.css";
import "./styles/index.scss"; // Import  styles
import theme from "./styles/theme";
import LayoutContainer from "~/containers/Layout";
import { reportError } from "~/utils/newrelic";

export const meta: Route.MetaFunction = () => {
    return [
        { title: "SCP Portal" },
        { name: "description", content: "Smart Contracting Platform Portal" },
        { charset: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
    ];
};

export const links: Route.LinksFunction = () => [
    { rel: "icon", href: sIcon, type: "image/svg+xml" },
    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
    },
    {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
    },
];

// Create emotion cache client-side
const emotionCache = createEmotionCache();

export function Layout({ children }: { children: React.ReactNode }) {
    return (
        <html lang="en">
            <head>
                <Meta />
                <meta name="emotion-insertion-point" content="" />
                <Links />
            </head>
            <body suppressHydrationWarning>
                <CacheProvider value={emotionCache}>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <LocalizationProvider dateAdapter={AdapterLuxon}>
                            <LayoutContainer>{children}</LayoutContainer>
                        </LocalizationProvider>
                    </ThemeProvider>
                </CacheProvider>
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    );
}

export default function App() {
    return <Outlet />;
}

export function HydrateFallback() {
    return null;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
    let message = "Oops!";
    let details = "An unexpected error occurred.";
    let stack: string | undefined;

    if (isRouteErrorResponse(error)) {
        message = error.status === 404 ? "404" : "Error";
        details =
            error.status === 404
                ? "The requested page could not be found."
                : error.statusText || details;

        // Report route errors (4xx/5xx) to New Relic
        reportError(new Error(`Route error ${error.status}: ${details}`), {
            type: "route_error",
            status: error.status,
            statusText: error.statusText,
        });
    } else if (error instanceof Error) {
        // Report unexpected JS errors to New Relic
        reportError(error, { type: "error_boundary" });

        if (import.meta.env.DEV) {
            details = error.message;
            stack = error.stack;
        }
    }

    return (
        <main
            style={{
                paddingTop: "4rem",
                padding: "1rem",
                maxWidth: "1200px",
                margin: "0 auto",
            }}
        >
            <h1>{message}</h1>
            <p>{details}</p>
            {stack && (
                <pre
                    style={{
                        width: "100%",
                        padding: "1rem",
                        overflowX: "auto",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                    }}
                >
                    <code>{stack}</code>
                </pre>
            )}
        </main>
    );
}
